/**
 * THE PAGE REGISTRY — one list of every page a user can reach, and the single
 * authority each one needs.
 *
 * WHY THIS EXISTS. Page visibility used to be declared in four places that had
 * no way to agree with each other:
 *   1. routing/nav-gating.ts       — SURFACE_BY_PATH + AUTHORITY_BY_PATH (the rail)
 *   2. features/crm/CrmLayout.tsx  — per-tab `capability` (the CRM tab bar)
 *   3. features/hotel-supplier/…   — per-tab `capability` (the HSM tab bar)
 *   4. expense/ExpenseLayout.tsx   — no gating at all
 * Admin "who can see what" therefore could not be answered truthfully: any
 * summary it rendered was a re-derivation that drifted the moment a tab moved.
 *
 * So the registry is the source and the consumers read from it:
 *   - the rail filter (gatePlatformNav)
 *   - each module's tab bar (moduleTabs)
 *   - the admin page-access editor + roster (pagesForRoles / rolesForPages)
 * Add a page HERE and every one of those updates together. That is the whole
 * point — an admin screen that describes access it doesn't control is worse than
 * no admin screen.
 *
 * NOT THE SECURITY BOUNDARY. Same rule as before: this drives UI affordances.
 * Every surface still enforces server-side (cm-api is deny-by-default, the
 * experts admin API is checkAdmin-gated). A page hidden here but reachable by
 * URL must still 403 at the API — the registry never becomes the only gate.
 */
import {
  deriveAuthorityKeys,
  RBAC_ROLES,
  rolesFromStored,
  type RbacRole,
  type SurfaceKey,
} from './rbac-model';

/** Top-level module a page belongs to — matches the left-rail grouping. */
export type PageModule =
  | 'chat'
  | 'crm'
  | 'hotel-supplier'
  | 'csm-staffing'
  | 'expense'
  | 'commissions'
  | 'admin';

export interface AppPage {
  /** Stable id. Persisted nowhere — it keys the admin picker's checkbox state. */
  key: string;
  /** Human label. This is what an admin reads, so it matches the tab exactly. */
  label: string;
  module: PageModule;
  /** Route path. Also the rail/tab target. */
  path: string;
  /** RBAC surface the user must hold ANY role on to reach this module at all. */
  surface: SurfaceKey;
  /**
   * The ONE authority this page needs, or null when holding any role on the
   * surface is enough. `null` is a real answer, not a TODO — Expense enforces at
   * the surface level today (its authorities mirror the labs Authority enum and
   * the store convergence is a tracked follow-on), so claiming a per-page
   * authority there would be fiction.
   */
  requires: string | null;
}

export const MODULE_LABEL: Record<PageModule, string> = {
  chat: 'Chat',
  crm: 'Customer Management',
  'hotel-supplier': 'Hotel Supplier Management',
  'csm-staffing': 'CSM Staffing',
  expense: 'Expense',
  commissions: 'Commissions',
  admin: 'Admin',
};

/**
 * Every page, in rail order then tab order. The `requires` values are copied
 * from the enforcement that exists today — CRM/HSM from their tab `capability`,
 * chat from use:chat, admin from platform:admin.
 */
export const APP_PAGES: readonly AppPage[] = [
  {
    key: 'chat',
    label: 'Chat',
    module: 'chat',
    path: '/chat',
    surface: 'chat',
    requires: 'use:chat',
  },

  {
    key: 'crm-home',
    label: 'Home',
    module: 'crm',
    path: '/crm/home',
    surface: 'crm',
    requires: 'view:home',
  },
  {
    // Gated on view:followups — the capability /api/work-queue actually enforces
    // — not view:home. It required view:home until 2026-09-08, so a role with
    // one and not the other was offered a tab that 403s on open. With the
    // Follow-ups page retired into this one, view:followups would otherwise
    // gate nothing at all, and the queue IS the follow-up surface now.
    key: 'crm-work-queue',
    label: 'Work Queue',
    module: 'crm',
    path: '/crm/work-queue',
    surface: 'crm',
    requires: 'view:followups',
  },
  {
    // Client CONTRACT renewals — not the Hotel Supplier rate renewals under
    // /hotel-supplier. Gated on view:account_health, matching /api/renewals:
    // it is a portfolio read over every customer's commercial position, so the
    // data-scoped Hotel Supplier role does not get it.
    key: 'crm-renewals',
    label: 'Renewals',
    module: 'crm',
    path: '/crm/renewals',
    surface: 'crm',
    requires: 'view:account_health',
  },
  {
    key: 'crm-customers',
    label: 'Customers',
    module: 'crm',
    path: '/crm/customers',
    surface: 'crm',
    requires: 'view:customers',
  },
  {
    key: 'crm-account-health',
    label: 'Account Health',
    module: 'crm',
    path: '/crm/account-health',
    surface: 'crm',
    requires: 'view:account_health',
  },
  {
    key: 'crm-segments',
    label: 'Segments & Lists',
    module: 'crm',
    path: '/crm/segments',
    surface: 'crm',
    requires: 'view:segments',
  },

  // Hotel Supplier, in HotelSupplierLayout's order. Every tab but one shares
  // view:supplier_tab — the module is gated as a unit and only Rate Opportunities
  // carries its own authority (cross-book sourcing intelligence, withheld from
  // crm:csm and crm:viewer per PR #1598).
  {
    key: 'hsm-customers',
    label: 'Customers',
    module: 'hotel-supplier',
    path: '/hotel-supplier/customers',
    surface: 'crm',
    requires: 'view:supplier_tab',
  },
  {
    key: 'hsm-hotels',
    label: 'Hotels',
    module: 'hotel-supplier',
    path: '/hotel-supplier/hotels',
    surface: 'crm',
    requires: 'view:supplier_tab',
  },
  {
    key: 'hsm-agreements',
    label: 'Agreements',
    module: 'hotel-supplier',
    path: '/hotel-supplier/agreements',
    surface: 'crm',
    requires: 'view:supplier_tab',
  },
  {
    key: 'hsm-rate-opportunities',
    label: 'Rate Opportunities',
    module: 'hotel-supplier',
    path: '/hotel-supplier/rate-opportunities',
    surface: 'crm',
    requires: 'view:supplier_rate_opportunities',
  },
  // The non-hotel vendor master (2026-10-08). AFTER the hotel workflow tabs
  // rather than inside them: customer → hotels → agreement → opportunities is
  // one lifecycle, and these are a separate reference list Vendor Relations
  // works on its own. Gated on view:supplier_tab like Hotels — a global
  // reference list, not cross-book intelligence.
  {
    key: 'hsm-airlines',
    label: 'Airlines',
    module: 'hotel-supplier',
    path: '/hotel-supplier/airlines',
    surface: 'crm',
    requires: 'view:supplier_tab',
  },
  {
    key: 'hsm-car-rental',
    label: 'Car Rental',
    module: 'hotel-supplier',
    path: '/hotel-supplier/car-rental',
    surface: 'crm',
    requires: 'view:supplier_tab',
  },

  // CSM Staffing. Surface 'crm', not a new one: the module reads customers,
  // booking volume and current owners out of the CRM, so a separate surface
  // would let someone hold staffing access without CRM access and still see
  // every customer name — the same reasoning cm-api's roles.ts records for
  // putting the three staffing authorities on the crm surface.
  //
  // Overview and Portfolios both gate on view:staffing. The financial figures
  // inside Overview are gated SEPARATELY and SERVER-SIDE by
  // view:staffing_financials — cm-api omits the keys rather than nulling them —
  // which is why there is no third page here for them.
  {
    key: 'csm-staffing-overview',
    label: 'Overview',
    module: 'csm-staffing',
    path: '/csm-staffing/overview',
    surface: 'crm',
    requires: 'view:staffing',
  },
  {
    key: 'csm-staffing-portfolios',
    label: 'Portfolios',
    module: 'csm-staffing',
    path: '/csm-staffing/portfolios',
    surface: 'crm',
    requires: 'view:staffing',
  },
  // `view:staffing`, not `manage:staffing`: the tab READS the team — who staffs
  // what, and whose book is whose. The editors inside it are gated separately
  // (manage:staffing to move a seat, and the SERVER's financial redaction for
  // the cost column), so gating the whole tab would hide the roster from the
  // people it describes in order to protect two controls.
  {
    key: 'csm-staffing-team',
    label: 'Team',
    module: 'csm-staffing',
    path: '/csm-staffing/team',
    surface: 'crm',
    requires: 'view:staffing',
  },

  // EXPENSE IS SURFACE-GATED, NOT PAGE-GATED — every entry below is `requires:
  // null` on purpose. ExpenseLayout renders its tab bar with no per-tab check,
  // so anyone holding an expense role sees all six tabs. Writing
  // `AUTHORITY_EXPENSE_APPROVER` on Approvals here would make the admin roster
  // claim an expense:user CANNOT see Approvals, which is false today. The
  // approver/user distinction is still grantable — it is a role, offered in the
  // editor's Advanced section, and enforcement lives in the expense API. When
  // ExpenseLayout starts gating tabs, set `requires` here in the same change.
  {
    key: 'expense-reports',
    label: 'Reports',
    module: 'expense',
    path: '/expense/reports',
    surface: 'expense',
    requires: null,
  },
  {
    key: 'expense-receipts',
    label: 'Receipt Inbox',
    module: 'expense',
    path: '/expense/receipts',
    surface: 'expense',
    requires: null,
  },
  {
    key: 'expense-banks',
    label: 'Banks',
    module: 'expense',
    path: '/expense/banks',
    surface: 'expense',
    requires: null,
  },
  {
    key: 'expense-mileage',
    label: 'Mileage',
    module: 'expense',
    path: '/expense/mileage',
    surface: 'expense',
    requires: null,
  },
  {
    key: 'expense-approvals',
    label: 'Approvals',
    module: 'expense',
    path: '/expense/approvals',
    surface: 'expense',
    requires: null,
  },
  {
    key: 'expense-admin',
    label: 'Expense Admin',
    module: 'expense',
    path: '/expense/admin',
    surface: 'expense',
    requires: null,
  },

  {
    // Commissions > Air: the commission review workbench. EXPLICIT GRANT ONLY:
    // review:commissions is not conferred by the platform admin role, so this
    // page is outside pagesForRoles(['admin']) on purpose, and the rail hides
    // the module from an admin without the grant (nav-gating
    // EXPLICIT_GRANT_ONLY_NAV). The UI additionally needs the
    // commissions.workbench.enabled flag (routing/nav-flags.ts). A Hotel tab
    // would be a sibling entry here (path /commissions/hotel).
    key: 'commissions-air',
    label: 'Air',
    module: 'commissions',
    path: '/commissions/air',
    surface: 'commissions',
    requires: 'review:commissions',
  },

  {
    key: 'admin',
    label: 'Admin (full platform access)',
    module: 'admin',
    path: '/admin',
    surface: 'platform',
    requires: 'platform:admin',
  },
] as const;

const PAGE_BY_KEY = new Map(APP_PAGES.map((p) => [p.key, p]));

/**
 * The modules the page editor and invite panel render, one box each, in rail
 * order. Only modules that HAVE pages: 'commissions' is a PageModule but has no
 * screen yet, and listing it here would render an empty box with a dead toggle.
 * Add it in the same change that registers its first page (page-registry.spec
 * fails until you do). When that page lands it must gate on
 * `review:commissions`, which the admin role deliberately lacks — so it will
 * NOT be in pagesForRoles(['admin']), and the "admin reaches every page" and
 * rolesForPages(['admin']) assertions need revisiting in that change.
 */
export const PAGE_MODULES: readonly PageModule[] = [
  'chat',
  'crm',
  'hotel-supplier',
  'csm-staffing',
  'expense',
  'commissions',
  'admin',
];

export function pageByKey(key: string): AppPage | undefined {
  return PAGE_BY_KEY.get(key);
}

/** Pages belonging to one module, in declaration order. */
export function pagesForModule(module: PageModule): AppPage[] {
  return APP_PAGES.filter((p) => p.module === module);
}

// ── roles → pages ───────────────────────────────────────────────────────────

/**
 * Can this (surface-set, authority-set) reach `page`? The two gates the rail and
 * the tab bars apply, in one function so they cannot disagree.
 */
function pageAllowed(
  page: AppPage,
  surfaces: ReadonlySet<SurfaceKey>,
  authorities: ReadonlySet<string>,
): boolean {
  if (!surfaces.has(page.surface)) return false;
  if (page.requires === null) return true;
  return authorities.has(page.requires);
}

/**
 * The pages a stored-role set can reach. This is the ONE answer the admin roster
 * and the rail both use.
 *
 * The platform-admin role confers platform:admin and every other surface's
 * authorities (see rbac-model), so it resolves to the full list through the
 * normal path — no special case, which is what keeps the display honest. The
 * exception is explicit-grant-only authorities (review:commissions): admin does
 * not hold them, so a page requiring one stays closed to an admin without the
 * grant. The surface widening below does not change that — `pageAllowed` still
 * checks the authority.
 */
export function pagesForRoles(storedRoles: readonly string[]): AppPage[] {
  const roles = rolesFromStored(storedRoles);
  const surfaces = new Set<SurfaceKey>(roles.map((r) => r.surface));
  const authorities = new Set(deriveAuthorityKeys(storedRoles));
  // A platform admin holds every surface, not just 'platform'.
  if (authorities.has('platform:admin')) {
    for (const p of APP_PAGES) surfaces.add(p.surface);
  }
  return APP_PAGES.filter((p) => pageAllowed(p, surfaces, authorities));
}

export function pageKeysForRoles(storedRoles: readonly string[]): string[] {
  return pagesForRoles(storedRoles).map((p) => p.key);
}

/** The pages ONE role grants on its own — used by the solver + the role list. */
export function pagesForRole(role: RbacRole): AppPage[] {
  return pagesForRoles([role.stored]);
}

// ── pages → roles (the admin picker's solver) ───────────────────────────────

export interface RoleSolution {
  /** Stored role strings to persist. */
  roles: string[];
  /** Every page those roles actually open. */
  granted: string[];
  /** Pages granted but NOT asked for — over-grant, surfaced to the admin. */
  extra: string[];
  /** Pages asked for that no role combination can grant. */
  unreachable: string[];
}

/**
 * Pick the smallest role set that covers the requested pages.
 *
 * Roles are the stored primitive (the server validates every string against the
 * spec and rejects unknowns), so the picker's job is to translate a page list
 * into roles. It is a set-cover, and set-cover can over-grant: 'crm:csm' is the
 * cheapest way to get Follow-ups, and it also opens Customers. We do NOT hide
 * that — `extra` is returned so the editor can show "this also opens …" before
 * the admin saves. Silent over-granting is the failure mode worth engineering
 * against here.
 *
 * Greedy, per surface: repeatedly take the role covering the most
 * still-uncovered requested pages, breaking ties by fewest extra pages. Greedy
 * is exact for every shape in the current model (each surface has ≤5 roles in a
 * near-total order) and degrades predictably if roles are added.
 */
export function rolesForPages(requestedKeys: readonly string[]): RoleSolution {
  const requested = requestedKeys
    .map((k) => PAGE_BY_KEY.get(k))
    .filter((p): p is AppPage => p != null);
  const requestedSet = new Set(requested.map((p) => p.key));

  // Admin is all-or-nothing: it confers every page, so asking for it ends the
  // search (any other role would be a no-op on top of it).
  //
  // Except explicit-grant-only pages (Commissions > Air): admin does not open
  // them, so any requested alongside Admin are solved separately and their
  // roles added, instead of being dropped silently.
  if (requestedSet.has('admin')) {
    const adminPages = new Set(pageKeysForRoles(['admin']));
    const rest = [...requestedSet].filter((k) => !adminPages.has(k));
    const restSolution = rest.length > 0 ? rolesForPages(rest) : null;
    const roles = ['admin', ...(restSolution?.roles ?? [])];
    const granted = pageKeysForRoles(roles);
    return {
      roles,
      granted,
      extra: granted.filter((k) => !requestedSet.has(k)),
      unreachable: restSolution?.unreachable ?? [],
    };
  }

  const chosen: string[] = [];
  const covered = new Set<string>();

  // Candidate roles, each with the page set it grants. 'admin' is excluded — it
  // is only ever chosen explicitly, above.
  const candidates = RBAC_ROLES.filter((r) => r.stored !== 'admin').map(
    (r) => ({ role: r, pages: new Set(pageKeysForRoles([r.stored])) }),
  );

  for (;;) {
    const missing = [...requestedSet].filter((k) => !covered.has(k));
    if (missing.length === 0) break;

    let best: { stored: string; gain: number; extra: number } | null = null;
    for (const c of candidates) {
      if (chosen.includes(c.role.stored)) continue;
      const gain = missing.filter((k) => c.pages.has(k)).length;
      if (gain === 0) continue;
      const extra = [...c.pages].filter((k) => !requestedSet.has(k)).length;
      if (
        best === null ||
        gain > best.gain ||
        (gain === best.gain && extra < best.extra)
      ) {
        best = { stored: c.role.stored, gain, extra };
      }
    }
    // Nothing left can cover the remainder — those pages are unreachable by
    // role (a page whose authority no non-admin role confers).
    if (best === null) break;

    chosen.push(best.stored);
    for (const k of candidates.find((c) => c.role.stored === best?.stored)
      ?.pages ?? []) {
      covered.add(k);
    }
  }

  const granted = pageKeysForRoles(chosen);
  const grantedSet = new Set(granted);
  return {
    roles: chosen,
    granted,
    extra: granted.filter((k) => !requestedSet.has(k)),
    unreachable: [...requestedSet].filter((k) => !grantedSet.has(k)),
  };
}

/**
 * The role set a PAGE edit should persist: the solver's roles plus every
 * baseline role that opens no page at all.
 *
 * A role that grants zero pages (commissions:reviewer today) can never come out
 * of `rolesForPages`, so saving the solver's answer verbatim would silently
 * revoke it on the first page edit — and an untouched editor would read as
 * dirty. Pages cannot express those roles, so the page editor leaves them as
 * they were; the Advanced role editor is where they are granted and revoked.
 * Unknown stored strings are not carried forward (the server rejects them).
 */
export function rolesToPersist(
  solvedRoles: readonly string[],
  baselineRoles: readonly string[],
): string[] {
  const out = [...solvedRoles];
  for (const role of rolesFromStored(baselineRoles)) {
    if (out.includes(role.stored)) continue;
    if (pageKeysForRoles([role.stored]).length === 0) out.push(role.stored);
  }
  return out;
}

// ── module tab bars read from here ──────────────────────────────────────────

/**
 * The tab list for a module, in the shape CrmModuleShell/ExpenseLayout consume.
 * `value` is the last path segment (what those layouts already used).
 *
 * Generic in the capability type so a caller can ask for its own narrower union
 * (CrmModuleShell wants `Capability` from features/crm/auth/roles). The registry
 * itself must not import the CRM types — it is the layer BELOW the modules — so
 * the narrowing is unchecked here and pinned in page-registry.spec.ts instead:
 * that spec asserts every CRM/HSM `requires` value is a member of
 * ALL_CAPABILITIES. Widen the union there and the test tells you.
 */
export function moduleTabs<C extends string = string>(
  module: PageModule,
): {
  value: string;
  text: string;
  path: string;
  capability?: C;
}[] {
  return pagesForModule(module).map((p) => ({
    value: p.path.split('/').filter(Boolean).slice(-1)[0] ?? p.key,
    text: p.label,
    path: p.path,
    ...(p.requires ? { capability: p.requires as C } : {}),
  }));
}
